class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.mapLoaded = false;
    this.listenersAttached = false;
  }

  connectedCallback() {
    // Wait for the map to load before attaching event listeners
    this.waitForMapLoad();
  }

  waitForMapLoad() {
    // Check if the map elements exist
    const retailerPill = document.querySelector(
      '.sc-pl-retailer-pill__container'
    );
    const searchBtn = document.querySelector(
      '.sc-pl-product-selector-form__content-search-btn'
    );

    if (retailerPill && searchBtn) {
      this.mapLoaded = true;
      this.attachEventListeners();
    } else {
      // Use MutationObserver to watch for dynamically added elements
      const observer = new MutationObserver((mutations, obs) => {
        const retailerPill = document.querySelector(
          '.sc-pl-retailer-pill__container'
        );
        const searchBtn = document.querySelector(
          '.sc-pl-product-selector-form__content-search-btn'
        );

        if (retailerPill && searchBtn && !this.listenersAttached) {
          this.mapLoaded = true;
          this.attachEventListeners();
          obs.disconnect(); // Stop observing once elements are found
        }
      });

      // Start observing the document body for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Fallback: Also check periodically in case MutationObserver misses it
      const checkInterval = setInterval(() => {
        const retailerPill = document.querySelector(
          '.sc-pl-retailer-pill__container'
        );
        const searchBtn = document.querySelector(
          '.sc-pl-product-selector-form__content-search-btn'
        );

        if (retailerPill && searchBtn && !this.listenersAttached) {
          this.mapLoaded = true;
          this.attachEventListeners();
          clearInterval(checkInterval);
          observer.disconnect();
        }
      }, 500);

      // Stop checking after 30 seconds to avoid infinite polling
      setTimeout(() => {
        clearInterval(checkInterval);
        observer.disconnect();
      }, 30000);
    }
  }

  attachEventListeners() {
    if (this.listenersAttached) return;

    // Track elements that have already fired events in the current click cycle
    this.processedElements = new Set();

    // Store reference to bound handler for cleanup
    this.clickHandler = (event) => {
      // Check if clicked element or its parent is a retailer pill
      const retailerPill = event.target.closest(
        '.sc-pl-retailer-pill__container'
      );
      if (retailerPill && !this.processedElements.has(retailerPill)) {
        // Mark as processed to prevent duplicate events
        this.processedElements.add(retailerPill);

        // Get the retailer name from the pill (inside the span)
        const retailerNameElement = retailerPill.querySelector('span');
        const retailerName = retailerNameElement?.textContent?.trim() || 'unknown';

        if (window.Shopify && window.Shopify.analytics) {
          window.Shopify.analytics.publish('retailer_click', {
            retailer_click: true,
            retailer_name: retailerName,
          });
        }

        // Clear the set after a short delay to allow future clicks
        setTimeout(() => {
          this.processedElements.delete(retailerPill);
        }, 100);
        return;
      }

      // Check if clicked element is the store locator search button
      const searchBtn = event.target.closest(
        '.sc-pl-product-selector-form__content-search-btn'
      );
      if (searchBtn && !this.processedElements.has(searchBtn)) {
        // Mark as processed to prevent duplicate events
        this.processedElements.add(searchBtn);

        if (window.Shopify && window.Shopify.analytics) {
          window.Shopify.analytics.publish('store_locator_click', {
            store_locator_click: true,
          });
        }

        // Clear the set after a short delay to allow future clicks
        setTimeout(() => {
          this.processedElements.delete(searchBtn);
        }, 100);
        return;
      }

      // Check if clicked element is the retailer list all button
      const retailerListAllBtn = event.target.closest(
        '.sc-pl-retailer-list__container-all-btn'
      );
      if (
        retailerListAllBtn &&
        !this.processedElements.has(retailerListAllBtn)
      ) {
        // Mark as processed to prevent duplicate events
        this.processedElements.add(retailerListAllBtn);

        // Get the button text and strip the count (e.g., "Online (1)" -> "Online")
        const retailerName = retailerListAllBtn.textContent?.trim().replace(/\s*\(\d+\)$/, '') || 'all';

        if (window.Shopify && window.Shopify.analytics) {
          window.Shopify.analytics.publish('retailer_click', {
            retailer_click: true,
            retailer_name: retailerName,
          });
        }

        // Clear the set after a short delay to allow future clicks
        setTimeout(() => {
          this.processedElements.delete(retailerListAllBtn);
        }, 100);
        return;
      }

      // Check if clicked element is the store offer CTA button (View Online)
      const storeOfferCtaBtn = event.target.closest(
        '.sc-pl-store-with-offer__container-cta-btn'
      );
      if (storeOfferCtaBtn && !this.processedElements.has(storeOfferCtaBtn)) {
        // Mark as processed to prevent duplicate events
        this.processedElements.add(storeOfferCtaBtn);

        // Get the retailer name from the parent container
        const storeContainer = storeOfferCtaBtn.closest(
          '.sc-pl-store-with-offer__container'
        );
        const retailerNameElement = storeContainer?.querySelector(
          '.sc-pl-store-with-offer__store-info-name'
        );
        const retailerName = retailerNameElement?.textContent?.trim() || 'unknown';

        if (window.Shopify && window.Shopify.analytics) {
          window.Shopify.analytics.publish('view_retailer', {
            view_retailer: true,
            retailer_name: retailerName,
          });
        }

        // Clear the set after a short delay to allow future clicks
        setTimeout(() => {
          this.processedElements.delete(storeOfferCtaBtn);
        }, 100);
        return;
      }
    };

    // Use event delegation on the document to catch all clicks
    // This handles dynamically added elements and prevents duplicate listeners
    document.addEventListener('click', this.clickHandler, true); // Use capture phase

    this.listenersAttached = true;
  }

  disconnectedCallback() {
    // Clean up event listener when component is removed
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler, true);
    }
    if (this.processedElements) {
      this.processedElements.clear();
    }
  }
}

customElements.define('store-locator', StoreLocator);
